import { type ReactNode } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteHeader, SiteFooter } from "@/components/SiteChrome";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { RelatedArticles, RelatedServices } from "@/components/RelatedLinks";
import { SITE } from "@/lib/seo";
import jsonLd from "@/data/eu-textile-epr-uk-firms-eu-sites-jsonld.json";

/**
 * Dedicated article route modelled on the ICO Met Police news route. Copy is the
 * approved article verbatim; JSON-LD is emitted exactly as supplied in the data file.
 */

const SLUG = "eu-textile-epr-uk-firms-eu-sites-uniforms";
const URL = `${SITE}/blog/${SLUG}`;
const TITLE_TAG = "EU Textile EPR for UK Firms with EU Sites | Preserve Earth";
const META_DESCRIPTION =
  "MEPs voted on 20 October to ease EU textile EPR paperwork for small EU producers. What it means for UK firms disposing of uniforms at EU sites.";
const H1 = "EU textile EPR: what should UK firms with sites in Europe know?";
const HERO_SRC = "/images/blog/eu-textile-epr-uk-firms-eu-sites-hero.webp";
const OG_IMAGE = `${SITE}/images/blog/eu-textile-epr-uk-firms-eu-sites-og.jpg`;
const HERO_ALT =
  "Operations manager packing folded plain hi-vis vests and dark work jackets into a cardboard box on a table, with a clipboard and parcel tape, as a colleague checks a tablet behind her";
const DATE = "2026-10-28";

const crumbs = [
  { name: "Blog", path: "/blog" },
  { name: H1, path: `/blog/${SLUG}` },
];

export const Route = createFileRoute("/blog/eu-textile-epr-uk-firms-eu-sites-uniforms")({
  head: () => ({
    meta: [
      { title: TITLE_TAG },
      { name: "description", content: META_DESCRIPTION },
      { property: "og:title", content: TITLE_TAG },
      { property: "og:description", content: META_DESCRIPTION },
      { property: "og:type", content: "article" },
      { property: "og:url", content: URL },
      { property: "og:image", content: OG_IMAGE },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:alt", content: HERO_ALT },
      { property: "article:published_time", content: DATE },
      { property: "article:modified_time", content: DATE },
      { property: "article:section", content: "Textile Recycling" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE_TAG },
      { name: "twitter:description", content: META_DESCRIPTION },
      { name: "twitter:image", content: OG_IMAGE },
      { name: "twitter:image:alt", content: HERO_ALT },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: (jsonLd as object[]).map((node) => ({
      type: "application/ld+json",
      children: JSON.stringify(node),
    })),
  }),
  component: EuTextileEprPage,
});

const linkClass =
  "underline decoration-moss/40 underline-offset-4 transition-colors hover:text-moss";
const h2Class = "mt-14 font-serif text-3xl leading-tight text-charcoal lg:text-4xl";
const bodyClass = "mt-5 text-pretty text-base leading-[1.8] text-charcoal/80";
const listClass =
  "mt-6 list-disc space-y-3 pl-6 text-base leading-[1.7] text-charcoal/80 marker:text-moss";

function A({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a href={href} className={linkClass}>
      {children}
    </a>
  );
}

function EuTextileEprPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader
        links={[
          { label: "Home", href: "/" },
          { label: "About", href: "/about" },
          { label: "Services", href: "/services" },
          { label: "Blog", href: "/blog" },
        ]}
        enquireHref="/#enquire"
      />

      <main>
        <article>
          <header className="container-page pt-16 pb-10">
            <Breadcrumbs crumbs={crumbs} />
            <p className="mb-6 text-xs font-medium uppercase tracking-[0.25em] text-moss">
              Textile Recycling · 6 min read
            </p>
            <h1 className="max-w-[28ch] text-balance font-serif text-4xl leading-[1.05] text-charcoal md:text-5xl lg:text-6xl">
              {H1}
            </h1>
            <p className="mt-6 text-xs uppercase tracking-[0.2em] text-charcoal/70">
              By Preserve Earth team, Last updated <time dateTime={DATE}>28 October 2026</time>
            </p>
          </header>

          <div className="container-page">
            <figure className="overflow-hidden rounded-md outline outline-1 -outline-offset-1 outline-charcoal/10">
              <img
                src={HERO_SRC}
                alt={HERO_ALT}
                width={1280}
                height={720}
                loading="eager"
                fetchPriority="high"
                className="aspect-[16/9] w-full object-cover"
              />
            </figure>
          </div>

          <div className="container-page py-20">
            <div className="grid gap-16 lg:grid-cols-12">
              <aside className="lg:col-span-3">
                <div className="sticky top-24 space-y-6 border-l border-moss/30 pl-6">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-moss">
                    The Ledger
                  </p>
                  <p className="text-sm leading-relaxed text-charcoal/70">
                    Field notes on compliant, documented disposal for UK businesses.
                  </p>
                  <Link
                    to="/blog"
                    className="block text-xs font-medium uppercase tracking-[0.2em] text-moss underline decoration-moss/30 underline-offset-4"
                  >
                    All articles
                  </Link>
                </div>
              </aside>

              <div className="lg:col-span-9 lg:max-w-[68ch]">
                <p className="text-pretty text-lg leading-[1.75] text-charcoal">
                  <strong>Short answer:</strong> On 20 October 2026 the European Parliament voted to
                  ease one piece of EU producer responsibility paperwork for textiles, footwear and
                  other products, but only for small EU-based producers, and it is not law yet. EU
                  textile extended producer responsibility (EPR) is aimed at companies that sell
                  clothing and footwear into EU markets, not at businesses throwing away their own
                  staff uniforms. If you are a UK firm with sites in France or Belgium, the
                  practical question is still the same one you have in the UK: how do you get rid of
                  branded workwear securely?
                </p>

                <section id="section-1" aria-labelledby="section-1-h">
                  <h2 id="section-1-h" className={h2Class}>
                    What did the European Parliament vote on?
                  </h2>
                  <p className={bodyClass}>
                    Under EU EPR rules, producers that place products on an EU market have to help
                    pay for collecting, sorting and recycling them once they become waste. A
                    producer selling into another EU country where it is not established also has to
                    appoint an authorised representative in that country to handle its EPR duties
                    there.
                  </p>
                  <p className={bodyClass}>
                    In December 2025 the European Commission proposed suspending that authorised
                    representative requirement until 1 January 2035 for batteries, packaging,
                    textiles, footwear, electrical and electronic equipment and some single-use
                    plastics. The Parliament's Environment Committee backed a narrower version on 5
                    October, and the full Parliament confirmed it in Strasbourg on 20 October.
                  </p>
                  <p className={bodyClass}>
                    The textiles, footwear, electronics and single-use plastics position passed by
                    612 votes to 8, with 39 abstentions. Under it:
                  </p>
                  <ul className={listClass}>
                    <li>
                      the suspension would apply only to micro and small producers established in
                      the EU, meaning fewer than 50 staff and turnover or balance sheet under 10
                      million euros
                    </li>
                    <li>
                      companies established outside the EU would still have to appoint
                      representatives
                    </li>
                    <li>
                      the suspension would end on 1 January 2035, or earlier if new EU circular
                      economy rules take over
                    </li>
                    <li>
                      producers would still have their other EPR duties, including paying towards
                      collection and recycling
                    </li>
                  </ul>
                  <p className={bodyClass}>
                    The vote sets Parliament's position for negotiations with EU governments. It
                    does not change the law on its own.
                  </p>
                </section>

                <section id="section-2" aria-labelledby="section-2-h">
                  <h2 id="section-2-h" className={h2Class}>
                    Where do things stand now?
                  </h2>
                  <p className={bodyClass}>
                    The two sides are some way apart. Recycling Europe, the EU recycling industry
                    body, said on 27 October that the Council's position covers packaging only, for
                    producers placing under one tonne a year in a member state, while Parliament's
                    covers more product types but only small firms. Recycling Europe wants the
                    requirement kept for everyone. According to the textile trade site TEXtalks, a
                    Council position on the textiles side was still outstanding as of 23 October.
                  </p>
                  <p className={bodyClass}>
                    None of this moves the main dates. The revised EU Waste Framework Directive
                    entered into force on 16 October 2025. EU countries must write it into national
                    law by 17 June 2027 and have textile and footwear EPR schemes in place by 17
                    April 2028.
                  </p>
                </section>

                <section id="section-3" aria-labelledby="section-3-h">
                  <h2 id="section-3-h" className={h2Class}>
                    Does EU textile EPR apply to your old uniforms?
                  </h2>
                  <p className={bodyClass}>
                    For most businesses, no. EPR under the directive falls on producers that make
                    textile and footwear products available on an EU market for the first time, such
                    as brands, manufacturers and importers. A logistics firm, hotel group or
                    security company that buys uniforms for its own staff and later retires them is
                    a user of those products, not the producer selling them.
                  </p>
                  <p className={bodyClass}>
                    That does not make old workwear risk free. Logo'd jackets, hi-vis and security
                    uniforms can be used to impersonate your staff, which is why we covered{" "}
                    <A href="https://preserveearth.co.uk/blog/branded-uniforms-textile-banks-secure-disposal">
                      why old branded uniforms should not go in textile banks
                    </A>
                    {". That risk is the same at a depot in Lille as at one in Leeds."}
                  </p>
                  <p className={bodyClass}>
                    Countries will set their own national rules when they write the directive into
                    law, so check local requirements for each site you run. We do not set out French
                    or Belgian rules here.
                  </p>
                </section>

                <section id="section-4" aria-labelledby="section-4-h">
                  <h2 id="section-4-h" className={h2Class}>
                    What this means for your business
                  </h2>
                  <ol className="mt-6 list-decimal space-y-3 pl-6 text-base leading-[1.7] text-charcoal/80 marker:text-moss">
                    <li>
                      <strong>Work out if you are a producer.</strong> If your group also sells
                      branded clothing or footwear into EU markets, for example merchandise, talk to
                      your compliance adviser about EPR. If you only buy uniforms for staff, EPR is
                      unlikely to be your duty.
                    </li>
                    <li>
                      <strong>List your workwear by site.</strong> Note where branded uniforms, PPE
                      and hi-vis are used and stored, in the UK and abroad.
                    </li>
                    <li>
                      <strong>Use one secure route.</strong> Avoid each site doing its own thing
                      with skips, textile banks or charity bags. Choose one secure process and
                      record each disposal.
                    </li>
                    <li>
                      <strong>Plan for EU sites.</strong> Collections are only part of the picture
                      if you have sites outside the UK, so agree how those items will be sent
                      securely.
                    </li>
                    <li>
                      <strong>Watch the dates.</strong> Keep 17 June 2027 and 17 April 2028 in your
                      diary, and check back once the EU negotiations finish.
                    </li>
                  </ol>
                </section>

                <section id="faq" aria-labelledby="faq-h">
                  <h2 id="faq-h" className={h2Class}>
                    Frequently asked questions
                  </h2>
                  <div className="mt-8 divide-y divide-charcoal/10 border-y border-charcoal/10">
                    <div className="py-6">
                      <h3 className="font-serif text-xl leading-snug text-moss">
                        Does EU textile EPR apply to a UK company's old uniforms?
                      </h3>
                      <p className="mt-3 text-base leading-[1.8] text-charcoal/80">
                        Not usually. EU textile EPR is aimed at producers that place textile and
                        footwear products on an EU market for the first time. A business disposing
                        of uniforms it bought for its own staff is not normally in that position,
                        but check local rules for each site.
                      </p>
                    </div>
                    <div className="py-6">
                      <h3 className="font-serif text-xl leading-snug text-moss">
                        When do EU countries have to have textile EPR schemes in place?
                      </h3>
                      <p className="mt-3 text-base leading-[1.8] text-charcoal/80">
                        By 17 April 2028. EU countries must first write the revised Waste Framework
                        Directive into national law by 17 June 2027.
                      </p>
                    </div>
                    <div className="py-6">
                      <h3 className="font-serif text-xl leading-snug text-moss">
                        Is the European Parliament's 20 October vote now law?
                      </h3>
                      <p className="mt-3 text-base leading-[1.8] text-charcoal/80">
                        No. It is Parliament's position for negotiations with EU governments, which
                        still have to agree a final text.
                      </p>
                    </div>
                    <div className="py-6">
                      <h3 className="font-serif text-xl leading-snug text-moss">
                        How do UK and EU sites differ for secure uniform disposal?
                      </h3>
                      <p className="mt-3 text-base leading-[1.8] text-charcoal/80">
                        For UK sites we arrange collections. For sites in France and Belgium,
                        clients courier their items securely to us in France, because collections
                        are offered in the UK only.
                      </p>
                    </div>
                  </div>
                </section>

                <aside
                  aria-labelledby="cta-h"
                  className="mt-20 border-y border-charcoal/15 bg-sage-light/40 px-8 py-10"
                >
                  <h2 id="cta-h" className="font-serif text-2xl text-charcoal">
                    Retiring uniforms across the UK and Europe?
                  </h2>
                  <p className="mt-3 max-w-[58ch] text-sm leading-relaxed text-charcoal/70">
                    We securely dispose of branded uniforms, PPE and workwear, with collections
                    across the UK, and clients in France and Belgium courier their items securely to
                    us in France. Tell us what you need to dispose of, where and how often, and we
                    will come back to you with a tailored quote.
                  </p>
                  <a
                    href="https://preserveearth.co.uk/services/textile-recycling#enquire"
                    className="mt-6 inline-flex items-center gap-3 rounded-sm bg-moss px-5 py-3 text-sm font-medium text-cream transition-colors hover:bg-charcoal"
                  >
                    Request a tailored quote
                  </a>
                </aside>

                <div className="mt-12 text-sm leading-relaxed text-charcoal/70">
                  <h2 className="text-[10px] font-semibold uppercase tracking-[0.25em] text-moss">
                    Sources
                  </h2>
                  <ul className="mt-3 space-y-2">
                    <li>
                      European Parliament, Texts adopted P10_TA(2026)0344, suspending the rules on
                      authorised representatives for EPR for waste, WEEE and single-use plastic
                      waste (20 October 2026):{" "}
                      <a
                        href="https://data.europarl.europa.eu/distribution/doc/TA-10-2026-0344_en.pdf"
                        target="_blank"
                        rel="noopener"
                        className={`${linkClass} break-all`}
                      >
                        https://data.europarl.europa.eu/distribution/doc/TA-10-2026-0344_en.pdf
                      </a>
                    </li>
                    <li>
                      European Parliament press release, "Waste: MEPs back simplified rules for
                      small businesses" (20 October 2026):{" "}
                      <a
                        href="https://www.europarl.europa.eu/news/en/press-room/20261018IPR48256/waste-meps-back-simplified-rules-for-small-businesses"
                        target="_blank"
                        rel="noopener"
                        className={`${linkClass} break-all`}
                      >
                        https://www.europarl.europa.eu/news/en/press-room/20261018IPR48256/waste-meps-back-simplified-rules-for-small-businesses
                      </a>
                    </li>
                    <li>
                      European Parliamentary Research Service, "Suspending authorised-representative
                      rules for extended producer responsibility" (October 2026):{" "}
                      <a
                        href="https://eprs.europarl.europa.eu/contents/publications/EPRS/2026/10/EPRS_ATA(2026)791541.html"
                        target="_blank"
                        rel="noopener"
                        className={`${linkClass} break-all`}
                      >
                        https://eprs.europarl.europa.eu/contents/publications/EPRS/2026/10/EPRS_ATA(2026)791541.html
                      </a>
                    </li>
                    <li>
                      Recycling Europe, "EP and Council positions on EPR authorised representative
                      undermine harmonisation and the single market" (27 October 2026):{" "}
                      <a
                        href="https://recyclingeurope.org/publication/ep-and-council-positions-on-epr-authorised-representative-undermine-harmonisation-and-the-single-market/"
                        target="_blank"
                        rel="noopener"
                        className={`${linkClass} break-all`}
                      >
                        https://recyclingeurope.org/publication/ep-and-council-positions-on-epr-authorised-representative-undermine-harmonisation-and-the-single-market/
                      </a>
                    </li>
                    <li>
                      TEXtalks, "EU parliament backs waste compliance relief for small textile
                      firms, but Non-EU exporters remain covered" (23 October 2026):{" "}
                      <a
                        href="https://textalks.com/eu-parliament-backs-waste-compliance-relief-for-small-textile-firms-but-non-eu-exporters-remain-covered/"
                        target="_blank"
                        rel="noopener"
                        className={`${linkClass} break-all`}
                      >
                        https://textalks.com/eu-parliament-backs-waste-compliance-relief-for-small-textile-firms-but-non-eu-exporters-remain-covered/
                      </a>
                    </li>
                    <li>
                      European Commission, "Revised Waste Framework Directive enters into force" (16
                      October 2025):{" "}
                      <a
                        href="https://environment.ec.europa.eu/news/revised-waste-framework-directive-enters-force-2025-10-16_en"
                        target="_blank"
                        rel="noopener"
                        className={`${linkClass} break-all`}
                      >
                        https://environment.ec.europa.eu/news/revised-waste-framework-directive-enters-force-2025-10-16_en
                      </a>
                    </li>
                    <li>
                      EUR-Lex, Directive (EU) 2025/1892 amending Directive 2008/98/EC on waste:{" "}
                      <a
                        href="https://eur-lex.europa.eu/eli/dir/2025/1892/oj/eng"
                        target="_blank"
                        rel="noopener"
                        className={`${linkClass} break-all`}
                      >
                        https://eur-lex.europa.eu/eli/dir/2025/1892/oj/eng
                      </a>
                    </li>
                  </ul>
                  <p className="mt-6 italic">
                    <em>
                      This article is general information based on European Parliament, European
                      Commission and EU legislation sources and trade reporting. It is not legal
                      advice.
                    </em>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </article>

        <RelatedArticles
          slugs={[
            "branded-uniforms-textile-banks-secure-disposal",
            "ico-confidential-waste-disposal-guide",
          ]}
        />
        <RelatedServices slugs={["textile-recycling"]} />
      </main>

      <SiteFooter />
    </div>
  );
}
